import React, { useState, useEffect } from "react";
import axios from "axios";
import { Building2, TrendingDown, Wallet, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("academiaos_token") || localStorage.getItem("token");
}

/**
 * StudentSponsorshipsView — student-facing FM4B sponsorship card.
 *
 * Displays the FM4B balance summary (student_collectible, sponsor coverage)
 * and per-sponsor assignment details.
 *
 * Invariant:
 *   student_collectible + active_sponsor_coverage = base_outstanding_before_sponsorship
 *
 * The most prominent amount shown to the student is "Your Responsibility" (student_collectible).
 * The full FM1 outstanding_balance is shown for context only, not as the amount due.
 */
export default function StudentSponsorshipsView({ embedded = false }) {
  const [summary, setSummary] = useState(null);
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [bal, spo] = await Promise.all([
          axios.get(`${API}/api/student/me/finance/fm4b/balance-summary`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          }),
          axios.get(`${API}/api/student/me/finance/fm4b/sponsorships`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          }),
        ]);
        setSummary(bal.data);
        setSponsorships(spo.data);
      } catch (e) {
        if (e.response?.status === 404) {
          // No charges recorded yet — this is a valid state
          setSummary({
            base_outstanding_before_sponsorship: "0",
            active_sponsor_coverage: "0",
            student_collectible: "0",
            institution_funded_reduction: "0",
          });
          setSponsorships([]);
        } else {
          setError(e.response?.data?.detail || "Could not load sponsorship data");
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="text-slate-400 text-sm py-4">Loading sponsorship details...</div>;
  if (error) return <div className="text-red-400 text-sm py-4">{error}</div>;
  if (!summary) return null;

  const hasActiveSponsor = Number(summary.active_sponsor_coverage) > 0;
  const fmt = (v) => `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6" data-testid="student-sponsorships-view">
      {/* Balance breakdown card */}
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-5">
          Fee Responsibility Summary
        </h2>

        <div className="space-y-4">
          {/* Total current obligation */}
          <div className="flex justify-between items-center py-3 border-b border-slate-800">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Wallet size={15} />
              <span>Total current obligation</span>
            </div>
            <span className="text-white font-medium" data-testid="base-outstanding">
              {fmt(summary.base_outstanding_before_sponsorship)}
            </span>
          </div>

          {/* Sponsor coverage */}
          {hasActiveSponsor && (
            <div className="flex justify-between items-center py-3 border-b border-slate-800">
              <div className="flex items-center gap-2 text-emerald-400 text-sm">
                <TrendingDown size={15} />
                <span>Covered by sponsor</span>
              </div>
              <span className="text-emerald-400 font-medium" data-testid="sponsor-coverage">
                − {fmt(summary.active_sponsor_coverage)}
              </span>
            </div>
          )}

          {/* Institution reductions (FM4A) */}
          {Number(summary.institution_funded_reduction) > 0 && (
            <div className="flex justify-between items-center py-3 border-b border-slate-800">
              <span className="text-slate-400 text-sm">Scholarships / Waivers applied</span>
              <span className="text-blue-400 font-medium">
                − {fmt(summary.institution_funded_reduction)}
              </span>
            </div>
          )}

          {/* Your responsibility — most prominent */}
          <div className={`flex justify-between items-center py-4 px-5 rounded-xl ${hasActiveSponsor ? "bg-emerald-950/50 border border-emerald-800/40" : "bg-slate-800"}`}>
            <span className="text-white font-semibold">Your responsibility</span>
            <span className="text-2xl font-bold text-white" data-testid="student-collectible">
              {fmt(summary.student_collectible)}
            </span>
          </div>
        </div>
      </div>

      {/* Per-sponsor breakdown */}
      {sponsorships.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Active Sponsorships</h3>
          <div className="space-y-3">
            {sponsorships.map(s => (
              <div key={s.id} data-testid={`sponsorship-${s.id}`}
                className="bg-slate-900 border border-slate-700 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-white font-medium text-sm">{s.sponsor_name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.assignment_ref}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-mono px-2 py-1 rounded-full ${
                    s.status === "ACTIVE" ? "bg-emerald-900/50 text-emerald-400" : "bg-slate-800 text-slate-400"
                  }`}>{s.status}</span>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-slate-500">Sponsor covers</div>
                    <div className="text-white font-medium text-sm mt-0.5">{fmt(s.sponsor_share)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Sponsor paid</div>
                    <div className="text-emerald-400 font-medium text-sm mt-0.5">{fmt(s.sponsor_paid)}</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock size={11} /> Sponsor pending
                    </div>
                    <div className={`font-medium text-sm mt-0.5 ${Number(s.sponsor_pending) > 0 ? "text-amber-400" : "text-slate-400"}`}>
                      {Number(s.sponsor_pending) > 0 ? fmt(s.sponsor_pending) : "Cleared"}
                    </div>
                  </div>
                </div>

                {Number(s.sponsor_pending) > 0 && (
                  <p className="mt-3 text-xs text-amber-300/80 bg-amber-950/30 border border-amber-800/30 rounded-lg px-3 py-2">
                    Sponsor funding pending — this amount is not part of your student liability.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {sponsorships.length === 0 && !hasActiveSponsor && (
        <p className="text-slate-500 text-sm">No active sponsorships on your account.</p>
      )}
    </div>
  );
}
